import React, { useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Grid,
  Typography,
  Card,
  CardContent,
  IconButton,
  Alert,
  LinearProgress,
  Chip
} from '@mui/material';
import { PhotoCamera, Delete } from '@mui/icons-material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { BasicInfo, KYCStatus } from '../types/kyc';
import { validateFile, FILE_CONSTRAINTS, ipfsUriToUrl } from '../utils/ipfs';
import ImagePreview from './ImagePreview';

interface BasicInfoFormProps {
  KYCStatus?:KYCStatus;
  initialData?: BasicInfo;
  onSubmit: (data: BasicInfo, files: { image?: File; banner?: File }) => Promise<void>;
  submitting: boolean;
}

// Yup验证规则
const validationSchema = Yup.object({
  name: Yup.string()
    .required('Name is required')
    .min(2, 'Name must be at least 2 characters')
    .max(50, 'Name cannot exceed 50 characters'),
  email: Yup.string()
    .email('Please enter a valid email address'),
  website: Yup.string()
    .matches(/^https?:\/\/.+/, 'Please enter a valid website address (starting with http:// or https://)'),
  description: Yup.string()
    .max(500, 'Description cannot exceed 500 characters'),
  social: Yup.object({
    twitter: Yup.string(),
    discord: Yup.string(),
    telegram: Yup.string()
  })
});

const BasicInfoForm: React.FC<BasicInfoFormProps> = ({
  KYCStatus,
  initialData,
  onSubmit,
  submitting
}) => {
  const [imageFile, setImageFile] = React.useState<File | null>(null);
  const [bannerFile, setBannerFile] = React.useState<File | null>(null);
  const [imagePreview, setImagePreview] = React.useState<string>('');
  const [bannerPreview, setBannerPreview] = React.useState<string>('');
  const [fileErrors, setFileErrors] = React.useState<Record<string, string>>({});

  const formik = useFormik<BasicInfo>({
    initialValues: {
      name: '',
      email: '',
      website: '',
      description: '',
      image: '',
      banner: '',
      social: {
        twitter: '',
        discord: '',
        telegram: ''
      }
    },
    validationSchema,
    onSubmit: async (values) => {
      // 清除文件错误
      setFileErrors({});
      
      // 准备文件数据
      const files: { image?: File; banner?: File } = {};
      if (imageFile) files.image = imageFile;
      if (bannerFile) files.banner = bannerFile;
      
      await onSubmit(values, files);
    }
  });

  useEffect(() => {
    if (initialData) {
      formik.setValues(initialData);
      // 如果有初始图片数据，设置预览
      if (initialData.image) {
        setImagePreview(ipfsUriToUrl(initialData.image));
      }
      if (initialData.banner) {
        setBannerPreview(ipfsUriToUrl(initialData.banner));
      }
    }
  }, [initialData]);

  // 创建文件预览URL
  const createPreviewUrl = (file: File): string => {
    return URL.createObjectURL(file);
  };

  // 清理预览URL
  const cleanupPreviewUrl = (url: string) => {
    if (url.startsWith('blob:')) {
      URL.revokeObjectURL(url);
    }
  };

  const handleFileSelect = (file: File, type: 'image' | 'banner') => {
    // 验证文件
    const validationError = validateFile(file, FILE_CONSTRAINTS.IMAGE.types, FILE_CONSTRAINTS.IMAGE.maxSize);
    if (validationError) {
      setFileErrors(prev => ({ ...prev, [type]: validationError }));
      return;
    }

    // 清除之前的预览URL
    if (type === 'image' && imagePreview.startsWith('blob:')) {
      cleanupPreviewUrl(imagePreview);
    }
    if (type === 'banner' && bannerPreview.startsWith('blob:')) {
      cleanupPreviewUrl(bannerPreview);
    }

    // 创建新的预览URL
    const previewUrl = createPreviewUrl(file);
    
    // 设置文件和预览
    if (type === 'image') {
      setImageFile(file);
      setImagePreview(previewUrl);
    } else {
      setBannerFile(file);
      setBannerPreview(previewUrl);
    }
    
    // 清除文件错误
    setFileErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[type];
      return newErrors;
    });
  };

  const handleRemoveFile = (type: 'image' | 'banner') => {
    if (type === 'image') {
      if (imagePreview.startsWith('blob:')) {
        cleanupPreviewUrl(imagePreview);
      }
      setImageFile(null);
      setImagePreview('');
      formik.setFieldValue('image', '');
    } else {
      if (bannerPreview.startsWith('blob:')) {
        cleanupPreviewUrl(bannerPreview);
      }
      setBannerFile(null);
      setBannerPreview('');
      formik.setFieldValue('banner', '');
    }
    
    // 清除文件错误
    setFileErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[type];
      return newErrors;
    });
  };

  // 清理组件卸载时的预览URL
  React.useEffect(() => {
    return () => {
      if (imagePreview.startsWith('blob:')) {
        cleanupPreviewUrl(imagePreview);
      }
      if (bannerPreview.startsWith('blob:')) {
        cleanupPreviewUrl(bannerPreview);
      }
    };
  }, [imagePreview, bannerPreview]);

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Basic Information
        </Typography>
        
        {formik.status && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {formik.status}
          </Alert>
        )}
        
        {submitting && (
          <Box sx={{ mb: 2 }}>
            <LinearProgress />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Submitting...
            </Typography>
          </Box>
        )}
        
        <Box component="form" onSubmit={formik.handleSubmit}>
        <Grid container spacing={3}>
          {/* 基本信息字段 */}
          <Grid item xs={12} md={6}>
            <TextField
              label="Name *"
              name="name"
              value={formik.values.name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              fullWidth
              error={formik.touched.name && Boolean(formik.errors.name)}
              helperText={formik.touched.name && formik.errors.name}
              disabled={submitting}
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              label="Email"
              name="email"
              type="email"
              value={formik.values.email}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              fullWidth
              error={formik.touched.email && Boolean(formik.errors.email)}
              helperText={formik.touched.email && formik.errors.email}
              disabled={submitting}
            />
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              label="Website"
              name="website"
              value={formik.values.website}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              fullWidth
              error={formik.touched.website && Boolean(formik.errors.website)}
              helperText={formik.touched.website && formik.errors.website}
              placeholder="https://example.com"
              disabled={submitting}
            />
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              label="Description"
              name="description"
              value={formik.values.description}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              fullWidth
              multiline
              rows={3}
              error={formik.touched.description && Boolean(formik.errors.description)}
              helperText={formik.touched.description && formik.errors.description}
              disabled={submitting}
            />
          </Grid>
          
          {/* 社交媒体字段 */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom>
              Social Media
            </Typography>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <TextField
              label="Twitter"
              name="social.twitter"
              value={formik.values.social?.twitter || ''}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              fullWidth
              placeholder="@username"
              disabled={submitting}
            />
          </Grid>
          
          <Grid item xs={12} md={4}>
            <TextField
              label="Discord"
              name="social.discord"
              value={formik.values.social?.discord || ''}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              fullWidth
              placeholder="username#1234"
              disabled={submitting}
            />
          </Grid>
          
          <Grid item xs={12} md={4}>
            <TextField
              label="Telegram"
              name="social.telegram"
              value={formik.values.social?.telegram || ''}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              fullWidth
              placeholder="@username"
              disabled={submitting}
            />
          </Grid>
          
          {/* 图片上传 */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom>
              Images
            </Typography>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Avatar Image
              </Typography>
              
              {imagePreview ? (
                <Box sx={{ position: 'relative', display: 'inline-block' }}>
                  <ImagePreview
                    src={imagePreview}
                    alt="Avatar Preview"
                    style={{ width: 100, height: 100, objectFit: 'cover', borderRadius: 8 }}
                  />
                  <IconButton
                    size="small"
                    sx={{ position: 'absolute', top: -8, right: -8, bgcolor: 'background.paper' }}
                    onClick={() => handleRemoveFile('image')}
                    disabled={submitting}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </Box>
              ) : (
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<PhotoCamera />}
                  disabled={submitting}
                >
                  Upload Avatar
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleFileSelect(file, 'image');
                    }}
                  />
                </Button>
              )}
              
              {fileErrors.image && (
                <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                  {fileErrors.image}
                </Typography>
              )}
            </Box>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Banner Image
              </Typography>
              
              {bannerPreview ? (
                <Box sx={{ position: 'relative', display: 'inline-block' }}>
                  <ImagePreview
                    src={bannerPreview}
                    alt="Banner Preview"
                    style={{ width: 400, height: 100, objectFit: 'cover', borderRadius: 8 }}
                  />
                  <IconButton
                    size="small"
                    sx={{ position: 'absolute', top: -8, right: -8, bgcolor: 'background.paper' }}
                    onClick={() => handleRemoveFile('banner')}
                    disabled={submitting}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </Box>
              ) : (
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<PhotoCamera />}
                  disabled={submitting}
                >
                  Upload Banner
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleFileSelect(file, 'banner');
                    }}
                  />
                </Button>
              )}
              
              {fileErrors.banner && (
                <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                  {fileErrors.banner}
                </Typography>
              )}
            </Box>
          </Grid>
          
          {/* 提交按钮 */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={submitting || !formik.values.name?.trim()||KYCStatus==2||KYCStatus==3}
              >
                {submitting ? 'Submitting...' : 'Submit Basic Information'}
              </Button>
            </Box>
          </Grid>
        </Grid>
        </Box>
      </CardContent>
    </Card>
  );
};

export default BasicInfoForm;